import { Box, Card, Stack } from '@mui/material';
import { RefObject, useMemo } from 'react';

import { TextEditorProvider } from '@/components/TextEditor/contexts/TextEditor.provider';
import { DocumentViewerWithSearch } from '@/modules/documents/components/DocumentViewerWithSearch';
import { EntityDocumentViewer_FilePageFragment } from '@/modules/documents/components/HouseholdDocumentsViewer/graphql/EntityDocumentViewer.generated';
import { DocumentDetailsViewOptions } from '@/modules/documents/documents.constants';
import { VIEWER_ONLY_DOCUMENT_TYPES } from '@/modules/documents/documentTypePolicies';
import { SelectableDocument } from '@/modules/documents/ManageDocumentAmendments/types';
import type { PDFSource } from '@/modules/pdf/PDFViewer';
import { DocumentType } from '@/types/schema';

import { useUnguardedDocumentDetails } from '../../contexts/DocumentDetails.context';
import { SummaryLoadingState } from '../../DocumentDetailsPage.types';
import { DocumentSummaryTabs } from '../../DocumentSummaryHeader';
import { DocumentDetailsDocumentViewer } from '../DocumentDetailsDocumentViewer';
import { NoSummaryView } from '../SummaryPanel/NoSummaryView';
import { ProcessingSummaryView } from '../SummaryPanel/ProcessingSummaryView';
import { SummaryContent } from '../SummaryPanel/SummaryContent';

const DOC_VIEWER_MIN_HEIGHT = 700;

interface DocumentViewerProps {
  documentId: string;
  source: PDFSource | null;
  documentFilePages: EntityDocumentViewer_FilePageFragment[];
  documentViewerHeight: string;
  aiEnabled: boolean;
  activeTab: DocumentDetailsViewOptions;
  setActiveTab: (tab: DocumentDetailsViewOptions) => void;
  viewerRef: RefObject<HTMLDivElement | null>;
  // Summary related props
  summaryLoadingState: SummaryLoadingState;
  onWriteSummary: () => void;
  onGenerateAiSummary: () => void;
  documentType?: DocumentType;
  associatedDocuments?: SelectableDocument[];
  onOpenChatWithSummary?: () => void;
}

export function DocumentViewer({
  documentId,
  source,
  documentFilePages,
  documentViewerHeight,
  aiEnabled,
  activeTab,
  setActiveTab,
  viewerRef,
  summaryLoadingState,
  onWriteSummary,
  onGenerateAiSummary,
  documentType,
  associatedDocuments,
  onOpenChatWithSummary,
}: DocumentViewerProps) {
  const ctx = useUnguardedDocumentDetails();
  const documentSummary = ctx?.documentSummary ?? null;

  const boxProps = useMemo(() => {
    return {
      ref: viewerRef,
      height: documentViewerHeight,
      minHeight: DOC_VIEWER_MIN_HEIGHT,
      overflow: 'hidden',
    };
  }, [documentViewerHeight, viewerRef]);

  // We don't care about showing tabs or any summary details for generated
  // presentations or document binders, and if AI functionality is off, we just
  // want to show the document viewer
  if (
    !aiEnabled ||
    (documentType != null && VIEWER_ONLY_DOCUMENT_TYPES.includes(documentType))
  ) {
    return (
      <Box {...boxProps}>
        <DocumentDetailsDocumentViewer
          loading={summaryLoadingState === SummaryLoadingState.Loading}
          documentId={documentId}
          source={source}
          documentFilePages={documentFilePages}
        />
      </Box>
    );
  }

  // Keep summary content and empty states hidden until the initial query resolves.
  const isLoading = summaryLoadingState === SummaryLoadingState.Loading;
  // Processing means the summary generation is in flight after the document loaded.
  const isProcessing = summaryLoadingState === SummaryLoadingState.Processing;

  if (activeTab === 'document') {
    return (
      <Box {...boxProps}>
        <DocumentViewerWithSearch
          loading={isLoading}
          documentId={documentId}
          source={source}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          sx={{
            backgroundColor: 'white',
            boxShadow: 'none',
          }}
          documentChromeProps={{
            shadowSx: {
              boxShadow: 'none',
            },
          }}
          filePages={documentFilePages}
          associatedDocuments={associatedDocuments}
          onOpenChatWithSummary={onOpenChatWithSummary}
        />
      </Box>
    );
  }

  return (
    <Card
      variant="outlined"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        // 610 aligns with the height of the AboutSection, so even when there's no
        // summary or a short summary, we want to show at least that much space.
        minHeight: 610,
      }}
    >
      <TextEditorProvider theme="userGeneratedContent">
        <Stack
          data-name="document-viewer-stack"
          spacing={2}
          p={2}
          flexGrow={1}
          aria-busy={isLoading}
        >
          {/* Always show tabs so users can switch to the Document view in any state */}
          <DocumentSummaryTabs
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
          {isProcessing && (
            <Stack flexGrow={1} justifyContent="center">
              <ProcessingSummaryView />
            </Stack>
          )}
          {summaryLoadingState === SummaryLoadingState.NoSummary && (
            <NoSummaryView
              onGenerateAiSummary={onGenerateAiSummary}
              onWriteSummary={onWriteSummary}
            />
          )}
          {summaryLoadingState === SummaryLoadingState.WithSummary &&
            documentSummary && (
              <SummaryContent
                documentId={documentId}
                documentType={documentType ?? null}
                onOpenChatWithSummary={onOpenChatWithSummary}
                onGenerateAiSummary={onGenerateAiSummary}
                onWriteSummary={onWriteSummary}
              />
            )}
        </Stack>
      </TextEditorProvider>
    </Card>
  );
}
